import React, { useEffect, useState } from "react";
import { MagnifyingGlass, UserPlus, UserMinus, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import VerifiedBadge from "@/components/VerifiedBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const avatar = (p) => p?.startsWith("/api/") ? `${BACKEND_URL}${p}` : p;

export default function Discover() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ following: 0, followers: 0 });
  const [search, setSearch] = useState("");

  const load = (q = "") => {
    api.get(`/users/search${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(({ data }) => setUsers(data));
  };
  const loadStats = () => api.get("/social/follows/stats").then(({ data }) => setStats(data));

  useEffect(() => { load(); loadStats(); }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const toggleFollow = async (u) => {
    try {
      if (u.is_following) {
        await api.delete(`/social/follow/${u.user_id}`);
        toast.success(`Unfollowed ${u.name}`);
      } else {
        await api.post("/social/follow", { target_user_id: u.user_id });
        toast.success(`Following ${u.name}`);
      }
      setUsers((us) => us.map((x) => x.user_id === u.user_id ? { ...x, is_following: !u.is_following } : x));
      loadStats();
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="discover-page">
      <div className="mb-8">
        <div className="label-tiny text-brand-accent mb-2">// SQUAD</div>
        <h1 className="font-heading text-4xl sm:text-5xl">DISCOVER ATHLETES</h1>
        <div className="flex gap-6 mt-4 text-sm">
          <div data-testid="stat-following">
            <span className="label-tiny mr-2">FOLLOWING</span>
            <span className="font-heading text-xl tabular-nums text-brand-accent">{stats.following}</span>
          </div>
          <div data-testid="stat-followers">
            <span className="label-tiny mr-2">FOLLOWERS</span>
            <span className="font-heading text-xl tabular-nums text-brand-accent">{stats.followers}</span>
          </div>
        </div>
      </div>

      <div className="relative mb-6">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
        <Input
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-surface-elevated border-white/10"
          data-testid="discover-search-input"
        />
      </div>

      {users.length === 0 ? (
        <div className="tactical-card p-12 text-center">
          <Users size={36} weight="duotone" className="text-brand-primary mx-auto mb-4" />
          <p className="text-text-secondary">No athletes found.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="users-list">
          {users.map((u) => (
            <div key={u.user_id} className="tactical-card p-4 flex items-center gap-3" data-testid={`user-card-${u.user_id}`}>
              {u.picture ? (
                <img src={avatar(u.picture)} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-brand-primary flex items-center justify-center font-heading">
                  {u.name?.[0]?.toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-heading text-base truncate inline-flex items-center gap-1.5">
                  {u.name?.toUpperCase()}
                  <VerifiedBadge verified={u.is_verified} size="xs" />
                </div>
                <div className="text-xs text-text-secondary truncate">{u.email}</div>
              </div>
              <button
                onClick={() => toggleFollow(u)}
                className={`px-3 py-2 text-xs label-tiny tracking-wider transition-colors flex items-center gap-1.5 ${
                  u.is_following
                    ? "border border-white/10 text-text-secondary hover:border-red-400 hover:text-red-400"
                    : "btn-kinetic"
                }`}
                data-testid={`follow-button-${u.user_id}`}
              >
                {u.is_following ? <><UserMinus size={12} /> UNFOLLOW</> : <><UserPlus size={12} /> FOLLOW</>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
